import React, { useEffect, useMemo, useState } from 'react';
import * as Bixby from '$/core';
import { connect } from 'react-redux';
import { AppState } from '$/store';
import { getCompany, setAlertsEnabled as setAlertsEnabledAction } from '$/store/portfolio/actions';
import { Routes, Route, useMatch, useNavigate } from 'react-router-dom';
import HeadBlock, { ITag, IToggle } from '$/components/global/heading';
import HeadTab from '$/components/global/headingTab';
import CompanyOverview from './CompanyOverview';
import CompanyNews from './CompanyNews';
import CompanyFinancials from './CompanyFinancials';
import CompanyKpis from './CompanyKpis';
import CompanyTranscriptions from './CompanyTranscriptions';
import {
  getIsCurrentUserAdminState,
  getIsCurrentUserClientComplianceAdminState,
  getIsCurrentUserClientState,
  getIsCurrentUserCreditSightsAnalystState,
  getIsCurrentUserCreditSightsOpsState,
  getIsCurrentUserRatingsAnalystState,
  getIsCurrentUserSuFAnalystState,
} from '$/store/profile/selectors';
import {
  CLIENT_COMPANY_TRANSCRIPTIONS_VIEW,
  CLIENT_COMPANY_DOCUMENTS,
  CLIENT_CREDITSIGHTS_COVENANT_REVIEW,
  CLIENT_COMPANY_COMPS,
  CLIENT_SFDR_VIEW,
} from '$/core/constants';
import CompanyDocuments from './CompanyDocuments';
import ClientCompanyDocumentsDownload from '$/components/client-company-documents-list/download';
import { getIsCurrentPortfolioCompanyPublicState } from '$/store/portfolio/selectors';
import {
  getDocumentHostingFeatureFlagState,
  getCreditSightsCovenantReviewFeatureFlagState,
  getHasBixbyCompanyCompsFeatureFlagState,
  getSFDREnabled,
  getCloPermissioningEnabled,
} from '$/store/release-feature-flags/selectors';
import GoPrivateButton from '$/components/go-private-button';
import CompanyCovenantReview from '$/pages/client/company/CompanyCovenantReview';
import { ReactComponent as Padlock } from '$/assets/icons/padlock.svg';
import CompanyComps from '$/pages/client/company/CompanyComps';
import {
  getHasBixbyComplianceAutoApprovalFeatureFlagState,
  getHasBixbyComplianceDemoModeFeatureFlagState,
  getHasBixbyComplianceFeatureFlagState,
} from '$/store/login/selectors';
import Loading from '$/components/loading/Loading';
import CompanyPai from '$/pages/client/company/CompanyPai';
import { DeclarationRequest } from '$/pages/client/company/declaration-request';
import './index.scss';

interface ICompanyProps {
  isCurrentPortfolioCompanyPublic: boolean;
  currentCompany?: Bixby.Models.Company;
  isLoading: boolean;
  showDraftToggle?: boolean;
  showAlertsToggle?: boolean;
  featureFlags?: string[];
  isDocumentHostingEnabled: boolean;
  hasBixbyComplianceFeatureFlag: boolean;
  hasBixbyComplianceAutoApprovalFeatureFlag: boolean;
  hasBixbyComplianceDemoModeFeatureFlag: boolean;
  isCreditSightsCovenantReviewEnabled: boolean;
  isCompanyCompEnabled: boolean;
  isSFDREnabled: boolean;
  isCloPermissioningEnabled: boolean;
  isClient: boolean;
  isClientComplianceAdmin: boolean;
  dispatchGetCurrentCompany: (portfolioCompany: Bixby.Models.CompanyPortfolio, draft: boolean) => Promise<void>;
  dispatchSetAlertsEnabled: (companyId: string, alertsEnabled: boolean) => Promise<void>;
}

const Company: React.FC<ICompanyProps> = props => {
  const {
    isCurrentPortfolioCompanyPublic,
    currentCompany,
    isLoading,
    showDraftToggle,
    showAlertsToggle,
    featureFlags = [],
    isDocumentHostingEnabled,
    hasBixbyComplianceFeatureFlag,
    hasBixbyComplianceAutoApprovalFeatureFlag,
    hasBixbyComplianceDemoModeFeatureFlag,
    isCreditSightsCovenantReviewEnabled,
    isCompanyCompEnabled,
    isSFDREnabled,
    isCloPermissioningEnabled,
    isClient,
    isClientComplianceAdmin,
    dispatchGetCurrentCompany,
    dispatchSetAlertsEnabled,
  } = props;

  const match = useMatch('/company/:companyId/*');

  const [alertsEnabled, setAlertsEnabled] = useState(false);

  const [declarationRequestModalActive, setDeclarationRequestModalActive] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    if (match?.params.companyId && match.params.companyId !== currentCompany?.id) {
      const setCompany = {
        companyId: match.params.companyId,
      } as Bixby.Models.CompanyPortfolio;
      dispatchGetCurrentCompany(setCompany, false);
    }
  }, [match, dispatchGetCurrentCompany, currentCompany?.id]);

  useEffect(() => {
    setAlertsEnabled((currentCompany && currentCompany.alertsEnabled) || false);
  }, [currentCompany]);

  const baseUrl = `/company/${match?.params.companyId}`;
  const newsUrl = `${baseUrl}/news`;
  const financialsUrl = `${baseUrl}/financials`;
  const kpisUrl = `${baseUrl}/kpis`;
  const transcriptionsUrl = `${baseUrl}/transcripts-replays`;
  const documentsUrl = `${baseUrl}/documents`;
  const covenantReviewUrl = `${baseUrl}/covenant-review`;
  const compsUrl = `${baseUrl}/comps`;
  const paiUrl = `${baseUrl}/pai`;
  const isDraft: boolean = (currentCompany && currentCompany.isDraft) || false;

  const draftToggle: IToggle | undefined = showDraftToggle
    ? {
        disabled: isLoading,
        checked: !isDraft,
        onChange: (isDraft: boolean) => {
          const setCompany = {
            companyId: match?.params.companyId,
          } as Bixby.Models.CompanyPortfolio;
          dispatchGetCurrentCompany(setCompany, isDraft);
        },
      }
    : undefined;
  const alertsToggle: IToggle | undefined = showAlertsToggle
    ? {
        checked: alertsEnabled,
        onChange: () => {
          if (!match?.params.companyId) return;
          dispatchSetAlertsEnabled(match.params.companyId, !alertsEnabled);
        },
      }
    : undefined;

  const tags = useMemo(() => {
    if (!hasBixbyComplianceFeatureFlag || !currentCompany) return undefined;

    const isPublic = currentCompany.declarationType === Bixby.Enums.DeclarationType.Public;
    return hasBixbyComplianceFeatureFlag &&
      isClient &&
      !currentCompany.isSECPublic &&
      (!hasBixbyComplianceDemoModeFeatureFlag || (hasBixbyComplianceDemoModeFeatureFlag && isClientComplianceAdmin))
      ? ([
          {
            text: isPublic ? 'Current Declaration: Public' : 'Current Declaration: Private',
            color: isPublic ? undefined : 'red',
            icon: isPublic ? undefined : <Padlock />,
          },
        ] as ITag[])
      : undefined;
  }, [
    hasBixbyComplianceFeatureFlag,
    hasBixbyComplianceDemoModeFeatureFlag,
    currentCompany,
    isClient,
    isClientComplianceAdmin,
  ]);

  const handleRequestDeclarationClose = async () => {
    setDeclarationRequestModalActive(false);
  };

  const handleRequestDeclarationSubmit = async () => {
    setDeclarationRequestModalActive(false);
    navigate('/portfolio');
  };

  const title =
    currentCompany && isCurrentPortfolioCompanyPublic
      ? `${currentCompany.name} | Ticker: ${currentCompany.publicStockTicker}`
      : currentCompany?.name;

  let conditionalLinkProps = {};
  if (!Bixby.Configuration.isStandAloneMode) {
    conditionalLinkProps = {
      target: '_blank',
    };
  }

  const titleElement = !currentCompany ? undefined : (
    <div>
      {currentCompany.creditSightsCompanyId ? (
        <div className="link">
          <a
            href={`${Bixby.Configuration.creditSightsUrl}/companies/${currentCompany.creditSightsCompanyId}`}
            {...conditionalLinkProps}>
            {title}
          </a>
        </div>
      ) : (
        title
      )}
    </div>
  );

  return currentCompany ? (
    <div className="company_detail">
      <HeadBlock
        titleElement={titleElement}
        HeadingActionButton={
          hasBixbyComplianceFeatureFlag &&
          isClient &&
          (!hasBixbyComplianceDemoModeFeatureFlag ||
            (hasBixbyComplianceDemoModeFeatureFlag && isClientComplianceAdmin)) &&
          currentCompany.declarationType === Bixby.Enums.DeclarationType.Public &&
          !currentCompany.isSECPublic &&
          (!isCloPermissioningEnabled ||
            (isCloPermissioningEnabled &&
              currentCompany.basisOfAccess !== Bixby.Enums.BasisOfAccess.CloHoldingsAccess)) &&
          !currentCompany.hasPrivateAccess ? (
            <GoPrivateButton text="Go Private" hasIcon onClick={() => setDeclarationRequestModalActive(true)} />
          ) : undefined
        }
        draftToggle={draftToggle}
        alertsToggle={alertsToggle}
        clientCompanyExportEnabled
        tags={tags}>
        <HeadTab title="Overview" link={baseUrl} />
        <HeadTab title="News" link={newsUrl} exact={true} />
        <HeadTab title="Financials" link={financialsUrl} exact={true} />
        <HeadTab title="KPIs" link={kpisUrl} exact={true} />
        {featureFlags.includes(CLIENT_COMPANY_TRANSCRIPTIONS_VIEW) && (
          <HeadTab title="Transcripts/Replays" link={transcriptionsUrl} exact={true} />
        )}
        {isDocumentHostingEnabled && featureFlags.includes(CLIENT_COMPANY_DOCUMENTS) && (
          <HeadTab title="Documents" link={documentsUrl} exact={true} />
        )}
        {isCreditSightsCovenantReviewEnabled &&
          featureFlags.includes(CLIENT_CREDITSIGHTS_COVENANT_REVIEW) &&
          currentCompany.declarationType === Bixby.Enums.DeclarationType.Public && (
            <HeadTab title="Covenant Review" link={covenantReviewUrl} exact={true} />
          )}
        {isCompanyCompEnabled && featureFlags.includes(CLIENT_COMPANY_COMPS) && (
          <HeadTab title="Comps" link={compsUrl} exact={true} />
        )}
        {isSFDREnabled && featureFlags.includes(CLIENT_SFDR_VIEW) && <HeadTab title="PAI" link={paiUrl} exact={true} />}
      </HeadBlock>
      <div>
        <Routes>
          <Route path={'/*'} element={<CompanyOverview />} />
          <Route path={'/news'} element={<CompanyNews />} />
          <Route path={'/financials'} element={<CompanyFinancials />} />
          <Route path={'/kpis'} element={<CompanyKpis />} />
          {featureFlags.includes(CLIENT_COMPANY_TRANSCRIPTIONS_VIEW) && (
            <Route path={'/transcripts-replays'} element={<CompanyTranscriptions />} />
          )}
          {isDocumentHostingEnabled && featureFlags.includes(CLIENT_COMPANY_DOCUMENTS) && (
            <>
              <Route path={'/documents'} element={<CompanyDocuments />} />
              <Route path={'/documents/:documentId/download'} element={<ClientCompanyDocumentsDownload />} />
            </>
          )}
          {isCreditSightsCovenantReviewEnabled && featureFlags.includes(CLIENT_CREDITSIGHTS_COVENANT_REVIEW) && (
            <Route path={'/covenant-review'} element={<CompanyCovenantReview />} />
          )}
          {isCompanyCompEnabled && featureFlags.includes(CLIENT_COMPANY_COMPS) && (
            <Route path={'/comps'} element={<CompanyComps />} />
          )}
          {isSFDREnabled && featureFlags.includes(CLIENT_SFDR_VIEW) && <Route path={'/pai'} element={<CompanyPai />} />}
        </Routes>
      </div>
      {declarationRequestModalActive && (
        <DeclarationRequest
          company_Id={currentCompany.id}
          company_Name={currentCompany.name}
          handleRequestDeclarationClose={handleRequestDeclarationClose}
          handleRequestDeclarationSubmit={handleRequestDeclarationSubmit}
          hasAutoApproval={hasBixbyComplianceAutoApprovalFeatureFlag}
        />
      )}
    </div>
  ) : isLoading ? (
    <Loading pageLevel />
  ) : null;
};

const mapStateToProps = (state: AppState) => {
  const { isAdmin } = getIsCurrentUserAdminState(state);
  const { isClient } = getIsCurrentUserClientState(state);
  const { isClientComplianceAdmin } = getIsCurrentUserClientComplianceAdminState(state);
  const { isCreditSightsAnalyst } = getIsCurrentUserCreditSightsAnalystState(state);
  const { isSuFAnalyst } = getIsCurrentUserSuFAnalystState(state);
  const { isRatingsAnalyst } = getIsCurrentUserRatingsAnalystState(state);
  const { isCreditSightsOps } = getIsCurrentUserCreditSightsOpsState(state);
  const { isCurrentPortfolioCompanyPublic } = getIsCurrentPortfolioCompanyPublicState(state);
  const { isDocumentHostingEnabled } = getDocumentHostingFeatureFlagState(state);
  const { isCreditSightsCovenantReviewEnabled } = getCreditSightsCovenantReviewFeatureFlagState(state);
  const { isCompanyCompEnabled } = getHasBixbyCompanyCompsFeatureFlagState(state);
  const { hasBixbyComplianceFeatureFlag } = getHasBixbyComplianceFeatureFlagState(state);
  const { hasBixbyComplianceAutoApprovalFeatureFlag } = getHasBixbyComplianceAutoApprovalFeatureFlagState(state);
  const { hasBixbyComplianceDemoModeFeatureFlag } = getHasBixbyComplianceDemoModeFeatureFlagState(state);
  const { isSFDREnabled } = getSFDREnabled(state);
  const { isCloPermissioningEnabled } = getCloPermissioningEnabled(state);

  return {
    isCurrentPortfolioCompanyPublic,
    currentCompany: state.portfolioState.currentCompany,
    isLoading: state.portfolioState.isLoading,
    showDraftToggle: isAdmin,
    showAlertsToggle: isClient && !isCreditSightsAnalyst && !isRatingsAnalyst && !isSuFAnalyst && !isCreditSightsOps,
    featureFlags: state.loginState.featureFlags,
    isDocumentHostingEnabled,
    hasBixbyComplianceFeatureFlag,
    hasBixbyComplianceAutoApprovalFeatureFlag,
    hasBixbyComplianceDemoModeFeatureFlag,
    isCreditSightsCovenantReviewEnabled,
    isCompanyCompEnabled,
    isSFDREnabled,
    isCloPermissioningEnabled,
    isClient,
    isClientComplianceAdmin,
  };
};

const mapDispatchToProps = (dispatch: any) => ({
  dispatchGetCurrentCompany: (company: Bixby.Models.CompanyPortfolio, draft: boolean) =>
    dispatch(getCompany(company, draft)),
  dispatchSetAlertsEnabled: (companyId: string, alertsEnabled: boolean) =>
    dispatch(setAlertsEnabledAction(companyId, alertsEnabled)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Company);
